import {Clone, GetEntries, AssertWarn} from "js-vextensions";
import {observable, action} from "mobx";
import React from "react";
import {CheckBox, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {InfoButton, O, RunInAction, RunInAction_Set} from "web-vcore";
import {UpdateMapObject} from "../../../../../Server/Commands/MapObjectCommands.js";
import {GetMapObject} from "../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {store} from "../../../../../Store/index.js";
import {GetCurrentMapControls, GetOpenMapID} from "../../../../../Store/main/maps.js";
import {GetMapState} from "../../../../../Store/main/maps/$mapState.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {Vec2, Vec2Simple, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {PolygonGuide} from "../PolygonGuide.js";
import {MapTool, Tool, ToolData, ToolInterceptSubresult} from "./@Tool.js";
import {GetAngleInDegreesOfDirection, GetAngleInDegreesOfLine, TransformObjLocalPointToWorldOffset_UsingRotationOnly, TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../../Utils/ThreeJS/SpaceUtils.js";
import {MapDragInfo} from "./Helpers/DragHelper.js";
import {MapControls} from "../../MapSurface/MapControls.js";
import {Get3JSCanvas} from "./Resize.js";
import {ObjPath} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {observer_mgl} from "mobx-graphlink";
import {ignore} from "mobx-sync";

export enum PathModifySubtool {
	none = "none",
	addToStart = "addToStart",
	addToEnd = "addToEnd",
	movePoints = "movePoints",
}

export class ToolState_PathModify {
	@O accessor subtool = PathModifySubtool.addToEnd;
	@action ChangeSubtool(subtool: PathModifySubtool) {
		this.subtool = subtool;
		if (subtool == PathModifySubtool.none) this.pendingObjPathUpdates.clear();
	}
	@O accessor snapToAngle_enabled = true;
	@O accessor snapToAngle_degrees = 15; // Default snap to 15 degrees
	@action SetSnapToAngleDegrees(degrees: number) {
		this.snapToAngle_degrees = Math.max(1, Math.min(180, degrees)); // Clamp between 1 and 180 degrees
	}

	@O @ignore accessor opInProgress = false;
	@O @ignore accessor pendingObjPathUpdates = observable.map<string, ObjPathUpdate>();
	@O @ignore accessor currentAngle_world: number = 0; // Current angle (of new line-segment, being previewed) in world space, used for display purposes
	@action StartOp() {
		if (this.opInProgress) return;
		this.opInProgress = true; // this allows updating of the visual-preview
	}
	@action EndCurrentOp(clearPendingUpdates = true) {
		if (this.opInProgress) {
			this.opInProgress = false; // this blocks updating of the visual-preview
			this.currentAngle_world = 0; // reset "current angle" display-variable
		}
		// have pending-update clearing occur regardless of operation-active status (since some tools show preview even prior to mouse-down/op-start)
		if (clearPendingUpdates) {
			// clear pending updates and reset cursor
			for (const update of this.pendingObjPathUpdates.values()) {
				if (update.op_movePoint?.dragInfo?.extras.canvas) {
					update.op_movePoint.dragInfo.extras.canvas.style.cursor = "";
				}
			}
			this.pendingObjPathUpdates.clear(); // this clears the visual preview
		}
	}
}

class ObjPathUpdate {
	constructor(data: RequiredBy<Partial<ObjPathUpdate>, "pathIndex">) { Object.assign(this, data); }
	pathIndex!: number;
	basePoints: Vec2Simple[] = [];

	// For "add point" operations
	op_addPoint?: {
		newPoint: Vec2Simple;
		insertPos: "start" | "end";
	};

	// For "move point" operations
	op_movePoint?: {
		pointIndex: number;
		dragInfo: MapDragInfo<{obj: MapObject, pathIndex: number, pointIndex: number, canvas: HTMLCanvasElement, controls: MapControls}>;
		// alt-insert mode: preview inserting a new point instead of moving the existing one
		altInsertMode?: boolean;
		insertAtIndex?: number; // index in basePoints at which to splice the new point
	};

	NewPoints() {
		// Move point operation
		if (this.op_movePoint) {
			const obj = this.op_movePoint.dragInfo.extras.obj;
			const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
			const objRot = ResolveObjRotInWorld.CatchBail(null, obj);

			// alt-insert mode: insert a new point rather than moving the existing one
			if (this.op_movePoint.altInsertMode && this.op_movePoint.insertAtIndex != null) {
				const newPoints = [...this.basePoints];
				if (objPos && objRot != null) {
					const worldOffset = this.op_movePoint.dragInfo.currentPos_world.Minus(objPos).ToVec2();
					let newPointPos_objSpace = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffset, objRot);
					const objPath = obj.paths[this.op_movePoint.dragInfo.extras.pathIndex];
					const guideSize = objPath?.guide_alignPoints && objPath?.guide_size ? objPath.guide_size : null;
					if (guideSize != null) {
						newPointPos_objSpace = new Vec2(newPointPos_objSpace.x.RoundTo(guideSize), newPointPos_objSpace.y.RoundTo(guideSize));
					}
					newPoints.splice(this.op_movePoint.insertAtIndex, 0, newPointPos_objSpace.ToSimple());
				}
				return newPoints;
			}

			// normal move mode
			const newPoints = [...this.basePoints];
			if (objPos && objRot != null) {
				// Convert world position to object-space position (accounting for rotation)
				const worldOffset = this.op_movePoint.dragInfo.currentPos_world.Minus(objPos).ToVec2();
				let newPointPos_objSpace = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffset, objRot);
				// Apply grid-alignment if enabled
				const objPath = obj.paths[this.op_movePoint.dragInfo.extras.pathIndex];
				const guideSize = objPath?.guide_alignPoints && objPath?.guide_size ? objPath.guide_size : null;
				if (guideSize != null) {
					newPointPos_objSpace = new Vec2(newPointPos_objSpace.x.RoundTo(guideSize), newPointPos_objSpace.y.RoundTo(guideSize));
				}
				newPoints[this.op_movePoint.pointIndex] = newPointPos_objSpace.ToSimple();
			}
			return newPoints;
		}
		// Add point operation
		if (this.op_addPoint) {
			if (this.op_addPoint.insertPos == "start") return [this.op_addPoint.newPoint, ...this.basePoints];
			if (this.op_addPoint.insertPos == "end") return [...this.basePoints, this.op_addPoint.newPoint];
		}
		throw new Error(`Invalid ObjPathUpdate configuration`);
	}
	sendStarted = false;
}

// Helper function to snap angle to nearest increment
function SnapAngleToIncrement(angleRadians: number, snapIncrementDegrees: number): number {
	const snapIncrementRadians = snapIncrementDegrees * Math.PI / 180;
	return Math.round(angleRadians / snapIncrementRadians) * snapIncrementRadians;
}

// Helper function to calculate angle-snapped position
function ApplyAngleSnapAndGridAlign(referencePoints: Vec2Simple[], targetPoint: Vec2, insertPos: "start" | "end", snapIncrementDegrees: number, guideSize?: number|n): Vec2 {
	let result = targetPoint;

	// apply angle-snapping
	const applyAngleSnapping = snapIncrementDegrees != 0 && referencePoints.length >= 1;
	if (applyAngleSnapping) {
		let innerOldPoint: Vec2, outerOldPoint: Vec2;
		if (insertPos == "end") {
			// Use last two points to determine line direction
			outerOldPoint = Vec2.FromSimple(referencePoints[referencePoints.length - 1]);
			innerOldPoint = referencePoints.length >= 2
				? Vec2.FromSimple(referencePoints[referencePoints.length - 2])
				: outerOldPoint.Plus(new Vec2(0, -1)); // if only one point, pretend the "existing line segment" had been going straight upwards
		} else { // insertPos == "start"
			// Use first two points to determine line direction
			outerOldPoint = Vec2.FromSimple(referencePoints[0]);
			innerOldPoint = referencePoints.length >= 2
				? Vec2.FromSimple(referencePoints[1])
				: outerOldPoint.Plus(new Vec2(0, -1)); // if only one point, pretend the "existing line segment" had been going straight upwards
		}

		// Calculate the existing line angle
		const existingDirection = outerOldPoint.Minus(innerOldPoint);
		const existingAngle = Math.atan2(existingDirection.y, existingDirection.x);

		// Calculate the angle from the last point to the target
		const targetDirection = result.Minus(outerOldPoint);
		const targetDistance = targetDirection.DistanceTo(new Vec2(0, 0))
		const targetAngle = Math.atan2(targetDirection.y, targetDirection.x);

		// Calculate the angle difference from the existing line
		let angleDiff = targetAngle - existingAngle;

		// Normalize angle difference to [-π, π]
		while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
		while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;

		// Snap the angle difference
		const snappedAngleDiff = SnapAngleToIncrement(angleDiff, snapIncrementDegrees);
		const snappedAngle = existingAngle + snappedAngleDiff;

		// Calculate the new position maintaining the target distance
		const snappedPosition = outerOldPoint.Plus(new Vec2(
			Math.cos(snappedAngle) * targetDistance,
			Math.sin(snappedAngle) * targetDistance
		));
		result = snappedPosition;
	}

	// apply grid-snapping
	if (guideSize != null && guideSize > 0) {
		// if not angle-snapping, then grid-snapping is very simple (and can be applied on both axes)
		if (!applyAngleSnapping) {
			result = new Vec2(result.x.RoundTo(guideSize), result.y.RoundTo(guideSize));
		}
		// if angle-snapping, then grid-snap only on one axis (whichever axis would require the smallest adjustment)
		else {
			// Get the reference point (the point we're drawing from)
			const referencePoint = insertPos == "end"
				? Vec2.FromSimple(referencePoints[referencePoints.length - 1])
				: Vec2.FromSimple(referencePoints[0]);

			const direction = result.Minus(referencePoint);
			const angle = Math.atan2(direction.y, direction.x);

			// Calculate what the final position would be if we snap each axis
			let finalPosIfSnapX: Vec2 | null = null;
			let finalPosIfSnapY: Vec2 | null = null;

			// Try snapping X to grid
			if (Math.abs(Math.cos(angle)) > 0.001) { // Avoid division by zero for vertical lines
				const gridSnappedX = result.x.RoundTo(guideSize);
				const distance = Math.abs(gridSnappedX - referencePoint.x) / Math.cos(angle);
				finalPosIfSnapX = referencePoint.Plus(new Vec2(
					Math.cos(angle) * distance * Math.sign(Math.cos(angle)),
					Math.sin(angle) * distance * Math.sign(Math.cos(angle))
				));
			}

			// Try snapping Y to grid
			if (Math.abs(Math.sin(angle)) > 0.001) { // Avoid division by zero for horizontal lines
				const gridSnappedY = result.y.RoundTo(guideSize);
				const distance = Math.abs(gridSnappedY - referencePoint.y) / Math.sin(angle);
				finalPosIfSnapY = referencePoint.Plus(new Vec2(
					Math.cos(angle) * distance * Math.sign(Math.sin(angle)),
					Math.sin(angle) * distance * Math.sign(Math.sin(angle))
				));
			}

			// Compare the actual movement distances and choose the smaller one
			let chooseSnapX = false;
			if (finalPosIfSnapX && finalPosIfSnapY) {
				const moveDistanceX = result.DistanceTo(finalPosIfSnapX);
				const moveDistanceY = result.DistanceTo(finalPosIfSnapY);
				chooseSnapX = moveDistanceX <= moveDistanceY;
			} else if (finalPosIfSnapX) {
				chooseSnapX = true;
			} else if (finalPosIfSnapY) {
				chooseSnapX = false;
			} else {
				// Can't snap either axis (shouldn't happen), leave result unchanged
				chooseSnapX = false;
			}

			// Apply the chosen snap
			if (chooseSnapX && finalPosIfSnapX) {
				result = finalPosIfSnapX;
			} else if (!chooseSnapX && finalPosIfSnapY) {
				result = finalPosIfSnapY;
			}
		}
	}

	return result;
}

function GetWorldAngleFromPendingObjPathUpdate(obj: MapObject, update: ObjPathUpdate): number | null {
	if (update.basePoints.length == 0) return null;
	if (!update.op_addPoint) return null; // Only calculate for add operations
	const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
	if (objPos == null) return null;
	const objRot = ResolveObjRotInWorld.CatchBail(null, obj);
	if (objRot == null) return null;

	// Determine which two points define the new line segment
	let point1: Vec2Simple, point2: Vec2Simple;

	if (update.op_addPoint.insertPos == "end") {
		point1 = update.basePoints[update.basePoints.length - 1];
		point2 = update.op_addPoint.newPoint;
	} else { // insertPos == "start"
		// for the purpose of angle-calculation, we always treat the existing point in the path as being the "start" point
		point1 = update.basePoints[0];
		point2 = update.op_addPoint.newPoint;
	}

	// Convert to Vec2 objects for calculations
	const vec1 = Vec2.FromSimple(point1);
	const vec2 = Vec2.FromSimple(point2);

	// Calculate the direction vector in object space
	const direction_objSpace = vec2.Minus(vec1);

	// Apply object rotation to get direction in world space
	const rotationRadians = objRot * Math.PI / 180; // assuming objRot is in degrees
	const direction_worldSpace = new Vec2(
		direction_objSpace.x * Math.cos(rotationRadians) - direction_objSpace.y * Math.sin(rotationRadians),
		direction_objSpace.x * Math.sin(rotationRadians) + direction_objSpace.y * Math.cos(rotationRadians)
	);

	return GetAngleInDegreesOfDirection(direction_worldSpace);
}

export class Tool_PathModify extends Tool {
	toolType = MapTool.pathModify;
	get ToolState() { return store.main.maps.tools.toolStates.pathModify; }

	RenderInToolbox = observer_mgl(({data}: {data: ToolData.RenderInToolbox})=>{
		const state = this.ToolState;
		return (
			<>
				<Select ml={5} displayType="button bar" childStyle={{padding: "3px 12px"}} options={GetEntries(PathModifySubtool, "ui")} value={state.subtool} onChange={val=>state.ChangeSubtool(val)}/>
				<InfoButton ml={5} text={`
					Hotkeys for subtool selection: hold ctrl = "add to start", hold shift = "add to end"
					Hotkey for "move points" subtool: hold alt to insert a new point instead of moving the existing one.
				`.AsMultiline(0)}/>
				<CheckBox ml={5} text="Snap to:" value={state.snapToAngle_enabled} onChange={val=>RunInAction_Set(()=>state.snapToAngle_enabled = val)}/>
				<Spinner ml={5} step={1} min={0} max={180} value={state.snapToAngle_degrees} onChange={val=>state.SetSnapToAngleDegrees(val)}/>
				<Text ml={5}>degrees</Text>
				{state.subtool !== PathModifySubtool.none && <Text ml={10}>(world angle: {state.currentAngle_world.toFixed(1)}°)</Text>}
			</>
		);
	});
	RenderInScene = observer_mgl_r3(({data}: {data: ToolData.RenderInScene})=>{
		const {map, mapState} = data;
		if (mapState.mousePos_world_0ms == null) return null;
		return <>
			<PolygonGuide {...{map, mapState}} forTool="pathModify"/>
		</>;
	});
	RenderInSceneOverlay = observer_mgl((props: {data: ToolData.RenderInSceneOverlay})=>{
		const state = this.ToolState;
		const mapID = GetOpenMapID.CatchBail(null);
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapID == null || mapState == null) return null;
		const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
		if (obj == null) return null;
		const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
		if (objPos == null) return null;
		const objRot = ResolveObjRotInWorld.CatchBail(null, obj);
		if (objRot == null) return null;
		const objPath = ObjPath.GetSelected(obj);
		if (objPath == null) return null;

		// Render red dots for each path point in movePoints mode
		if (state.subtool == PathModifySubtool.movePoints) {
			const pendingUpdate = state.pendingObjPathUpdates.get(obj.id);
			const drag = pendingUpdate?.op_movePoint?.dragInfo;
			const controls_DOM = (drag?.extras.controls ?? GetCurrentMapControls())?.DOM;
			if (!controls_DOM) return null;

			// ensure re-render on camera move/zoom
			const _extraMobxTrackers = [mapState.camPos_0ms, mapState.camZoom_0ms];

			const altInsertMode = pendingUpdate?.op_movePoint?.altInsertMode ?? false;
			const draggedIndex = drag?.extras.pointIndex ?? -1;
			return <>
				{objPath.points.map((point, index)=>{
					const point_withObjRotApplied = TransformObjLocalPointToWorldOffset_UsingRotationOnly(Vec2.FromSimple(point), objRot).ToVec3(0);
					const point_world = objPos.Plus(point_withObjRotApplied);
					const point_screen = controls_DOM.WorldPosToClientPos(point_world.ToVec2());

					// In normal drag mode, show the dragged point at the current mouse position.
					// In alt-insert mode, the original point stays put; the new point is shown separately below.
					let displayPos_screen = point_screen;
					if (drag && draggedIndex === index && !altInsertMode) {
						displayPos_screen = controls_DOM.WorldPosToClientPos(drag.currentPos_world.ToVec2());
					}

					return <div key={index} style={{
						position: "fixed",
						left: `${displayPos_screen.x - 4}px`,
						top: `${displayPos_screen.y - 4}px`,
						width: "8px",
						height: "8px",
						borderRadius: "50%",
						backgroundColor: "red",
						pointerEvents: "none",
						zIndex: 100,
					}}/>;
				})}
				{/* In alt-insert mode, show a green preview dot at the current mouse position */}
				{altInsertMode && drag && (()=>{
					const newPos_screen = controls_DOM.WorldPosToClientPos(drag.currentPos_world.ToVec2());
					return <div key="insert-preview" style={{
						position: "fixed",
						left: `${newPos_screen.x - 5}px`,
						top: `${newPos_screen.y - 5}px`,
						width: "10px",
						height: "10px",
						borderRadius: "50%",
						backgroundColor: "limegreen",
						pointerEvents: "none",
						zIndex: 101,
					}}/>;
				})()}
			</>;
		}

		return null;
	});

	Tool_OnDeactivate(data: ToolData.Tool_OnDeactivate): void {
		this.ToolState.EndCurrentOp();
	}

	shiftToggleActive = false;
	ctrlToggleActive = false;
	Document_OnKeyDown(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Control" && !this.ctrlToggleActive) {
			this.ToolState.ChangeSubtool(PathModifySubtool.addToStart);
			this.ctrlToggleActive = true;
			return {absorb: true};
		}
		if (data.event.key == "Shift" && !this.shiftToggleActive) {
			this.ToolState.ChangeSubtool(PathModifySubtool.addToEnd);
			this.shiftToggleActive = true;
			return {absorb: true};
		}

		// if escape-key is pressed, cancel the current mouse-drag operation, and clear any pending mesh updates
		if (data.event.key == "Escape") {
			this.ToolState.EndCurrentOp();
			return {absorb: true};
		}
	}
	Document_OnKeyUp(data: ToolData.Document_OnKeyDown): ToolInterceptSubresult | void {
		if (data.event.key == "Control" && this.ctrlToggleActive) {
			this.ToolState.ChangeSubtool(PathModifySubtool.none);
			this.ctrlToggleActive = false;
			return {absorb: true};
		}
		if (data.event.key == "Shift" && this.shiftToggleActive) {
			this.ToolState.ChangeSubtool(PathModifySubtool.none);
			this.shiftToggleActive = false;
			return {absorb: true};
		}
	}

	Scene_OnPointerDown(data: ToolData.Scene_OnPointerDown) {
		if (data.event.button != 0) return; // left mouse-button only

		const state = this.ToolState;

		// Handle move points subtool
		if (state.subtool == PathModifySubtool.movePoints) {
			const {event, mousePos, mousePos_world, controls} = data;
			const mapID = GetOpenMapID.CatchBail(null);
			const mapState = GetMapState.CatchBail(null, mapID);
			if (mapID == null || mapState == null) return;
			const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
			if (obj == null) return;
			const objPath = ObjPath.GetSelected(obj);
			if (objPath == null) return;

			const nearPoint = this.GetNearestPointIfClose(obj, objPath, mousePos_world, controls);
			if (nearPoint != null) {
				const canvas = Get3JSCanvas();
				if (!canvas) return;

				const dragInfo: MapDragInfo<{obj: MapObject, pathIndex: number, pointIndex: number, canvas: HTMLCanvasElement, controls: MapControls}> = new MapDragInfo({
					button: event.button,
					startPos_mouse: mousePos,
					startPos_world: mousePos_world,
					currentPos_mouse: mousePos,
					currentPos_world: mousePos_world,
					extras: {
						obj: obj,
						pathIndex: obj.paths.indexOf(objPath),
						pointIndex: nearPoint,
						canvas,
						controls,
					},
				});

				RunInAction("PathModify.Scene_OnPointerDown", ()=>{
					state.pendingObjPathUpdates.set(obj.id, new ObjPathUpdate({
						pathIndex: obj.paths.indexOf(objPath),
						basePoints: objPath.points,
						op_movePoint: {
							pointIndex: nearPoint,
							dragInfo: dragInfo,
						},
					}));
				});
			}
		}

		this.ToolState.StartOp();
	}
	Scene_OnPointerMove(data: ToolData.Scene_OnPointerMove) {
		const {mousePos_world, mousePos, controls} = data;
		//if (!this.ToolState.opInProgress) return; // commented; we want preview to show before op started

		const mapID = GetOpenMapID.CatchBail(null);
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapID == null || mapState == null) return;
		const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
		if (obj == null) return;
		const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
		if (objPos == null) return;
		const objPath = ObjPath.GetSelected(obj);
		if (objPath == null) return;
		const state = this.ToolState;

		if (state.subtool == PathModifySubtool.addToStart || state.subtool == PathModifySubtool.addToEnd) {
			const guideSize = objPath.guide_alignPoints && objPath.guide_size ? objPath.guide_size : null;
			const targetPoint_objSpace = mousePos_world.Minus(objPos).ToVec2();

			const prevPendingUpdate = state.pendingObjPathUpdates.get(obj.id);
			const basePoints = prevPendingUpdate?.sendStarted ? prevPendingUpdate.basePoints : objPath.points;
			const targetPoint_objSpace_withAlignments = ApplyAngleSnapAndGridAlign(
				basePoints, targetPoint_objSpace, state.subtool == PathModifySubtool.addToEnd ? "end" : "start",
				state.snapToAngle_enabled ? state.snapToAngle_degrees : 0,
				guideSize,
			);

			const newPendingUpdate = new ObjPathUpdate({
				pathIndex: obj.paths.indexOf(objPath),
				basePoints: basePoints,
				op_addPoint: {
					newPoint: targetPoint_objSpace_withAlignments.ToSimple(),
					insertPos: state.subtool == PathModifySubtool.addToEnd ? "end" : "start",
				},
			});
			const worldAngle = GetWorldAngleFromPendingObjPathUpdate(obj, newPendingUpdate); // Calculate the world angle for display

			RunInAction("PathModify.Scene_OnPointerMove", ()=>{
				state.pendingObjPathUpdates.set(obj.id, newPendingUpdate)
				if (worldAngle != null) {
					state.currentAngle_world = worldAngle;
				}
			});
		} else if (state.subtool == PathModifySubtool.movePoints) {
			const canvas = Get3JSCanvas();

			if (state.opInProgress) {
				const pendingUpdate = state.pendingObjPathUpdates.get(obj.id);
				const drag = pendingUpdate?.op_movePoint?.dragInfo;
				if (!drag) {
					// commented warning; this can happen if user starts new op, while previous op is still progressing // todo: find better solution
					//AssertWarn(false, "Path-modify move-points op in progress, yet no pending-update found!");
					return;
				}

				// Compute alt-insert-mode fields if alt is held
				const altInsertMode = data.event.altKey;
				let insertAtIndex: number | undefined;
				if (altInsertMode) {
					const pi = drag.extras.pointIndex;
					const objPos2 = ResolveObjPosInWorld.CatchBail(null, obj);
					const objRot2 = ResolveObjRotInWorld.CatchBail(null, obj);
					if (objPos2 && objRot2 != null) {
						const toWorldPos = (p: Vec2Simple) =>
							objPos2.Plus(TransformObjLocalPointToWorldOffset_UsingRotationOnly(Vec2.FromSimple(p), objRot2).ToVec3(0)).ToVec2();
						const nPts = objPath.points.length;
						const isClosed = objPath.curve_closed === true;

						// Build the list of candidate neighbors. Each carries its world-position (for angle
						// comparison against the drag direction) and the splice index to use if that neighbor wins.
						// For closed paths, the first/last points wrap around; inserting in the wrap gap = append (index nPts).
						type Neighbor = {worldPos: Vec2, insertAt: number};
						const neighbors: Neighbor[] = [];
						if (pi > 0)                        neighbors.push({worldPos: toWorldPos(objPath.points[pi - 1]),     insertAt: pi});
						else if (isClosed && nPts >= 3)    neighbors.push({worldPos: toWorldPos(objPath.points[nPts - 1]),   insertAt: nPts});
						if (pi < nPts - 1)                 neighbors.push({worldPos: toWorldPos(objPath.points[pi + 1]),     insertAt: pi + 1});
						else if (isClosed && nPts >= 3)    neighbors.push({worldPos: toWorldPos(objPath.points[0]),          insertAt: nPts});

						// Pick the neighbor whose direction (from the dragged point's ORIGINAL position) best
						// matches the angle the user is dragging in. Falls back to closest-anchor when the drag
						// is too small to have a meaningful direction (e.g. on pointer-down).
						const mouse2d = mousePos_world.ToVec2();
						const origPos2d = toWorldPos(objPath.points[pi]);
						const dragVec = mouse2d.Minus(origPos2d);
						const dragLen = Math.sqrt(dragVec.x * dragVec.x + dragVec.y * dragVec.y);
						let best: Neighbor | undefined;
						if (dragLen > 1e-4 && neighbors.length >= 1) {
							const ddx = dragVec.x / dragLen, ddy = dragVec.y / dragLen;
							let bestDot = -Infinity;
							for (const nb of neighbors) {
								const nbRaw = nb.worldPos.Minus(origPos2d);
								const nbLen = Math.sqrt(nbRaw.x * nbRaw.x + nbRaw.y * nbRaw.y);
								if (nbLen < 1e-4) continue;
								const dot = ddx * (nbRaw.x / nbLen) + ddy * (nbRaw.y / nbLen);
								if (dot > bestDot) { bestDot = dot; best = nb; }
							}
						}
						if (!best) {
							best = neighbors.sort((a, b)=>mouse2d.DistanceTo(a.worldPos) - mouse2d.DistanceTo(b.worldPos))[0];
						}
						if (best) insertAtIndex = best.insertAt;
					}
				}

				// if drag is active, rewrite the "pending update" with a new entry (so mobx observers see the change)
				const newUpdate = new ObjPathUpdate({
					...pendingUpdate!,
					op_movePoint: {
						...pendingUpdate!.op_movePoint!,
						dragInfo: new MapDragInfo<{obj: MapObject, pathIndex: number, pointIndex: number, canvas: HTMLCanvasElement, controls: MapControls}>({
							button: drag.button,
							startPos_mouse: drag.startPos_mouse,
							startPos_world: drag.startPos_world,
							currentPos_mouse: mousePos,
							currentPos_world: mousePos_world,
							extras: drag.extras,
						}),
						altInsertMode,
						insertAtIndex,
					},
				});
				RunInAction("PathModify.Scene_OnPointerMove", ()=>{
					state.pendingObjPathUpdates.set(obj.id, newUpdate);
				});
				if (canvas) canvas.style.cursor = altInsertMode ? "copy" : "move";
			} else {
				// Update cursor based on proximity to points
				if (canvas) {
					const nearPoint = this.GetNearestPointIfClose(obj, objPath, mousePos_world, controls);
					canvas.style.cursor = nearPoint != null ? "move" : "default";
				}
			}
		}
	}
	Document_OnPointerUp(data: ToolData.Document_OnPointerUp) {
		if (data.event.button != 0) return; // left mouse-button only
		if (!this.ToolState.opInProgress) return;
		//this.ToolState.opInProgress = false;

		const state = this.ToolState;
		const mapID = GetOpenMapID.CatchBail(null);
		const mapState = GetMapState.CatchBail(null, mapID);
		if (mapID == null || mapState == null) return;
		const obj = GetMapObject.CatchBail(null, mapState.activeObject, mapID);
		if (obj == null) return;

		if (state.subtool == PathModifySubtool.addToStart || state.subtool == PathModifySubtool.addToEnd) {
			const pendingUpdate = state.pendingObjPathUpdates.get(obj.id);
			if (pendingUpdate != null) {
				PushPendingPathUpdateForObj(obj, pendingUpdate, state);
			}
		} else if (state.subtool == PathModifySubtool.movePoints) {
			const pendingUpdate = state.pendingObjPathUpdates.get(obj.id);
			const drag = pendingUpdate?.op_movePoint?.dragInfo;
			if (drag && drag.button == data.event.button) {
				// In alt-insert mode, require a minimum drag distance before applying.
				// This guards against spurious pointerup events (e.g. Linux WMs that intercept
				// Alt+LMB drag and emit a synthetic pointerup while the mouse is still held).
				const dragDist = drag.currentPos_mouse.DistanceTo(drag.startPos_mouse);
				if (pendingUpdate!.op_movePoint!.altInsertMode && dragDist < 3) {
					// Spurious pointerup — cancel without applying, then reset so user can try again
					this.ToolState.EndCurrentOp(true);
					return;
				}
				PushPendingPathUpdateForObj(obj, pendingUpdate, state);
			}
		}

		this.ToolState.EndCurrentOp(false); // false, to not clear pending-update (we wait to do that when PushPendingPathUpdateForObj finishes sending the update)
	}

	/** Returns the index of the nearest point if it's within 5 pixels, otherwise null */
	GetNearestPointIfClose(obj: MapObject, objPath: ObjPath, mousePos_world: Vec3, controls: MapControls): number | null {
		const controls_DOM = controls.DOM;
		if (!controls_DOM) return null;
		const objPos = ResolveObjPosInWorld.CatchBail(null, obj);
		if (objPos == null) return null;
		const objRot = ResolveObjRotInWorld.CatchBail(null, obj);
		if (objRot == null) return null;

		const maxDist = 5 * controls_DOM.OnePixelAsMeters().x; // 5 pixels, converted to meters
		let closestIndex: number | null = null;
		let closestDist = Infinity;

		for (let i = 0; i < objPath.points.length; i++) {
			const point = objPath.points[i];
			const point_withObjRotApplied = TransformObjLocalPointToWorldOffset_UsingRotationOnly(Vec2.FromSimple(point), objRot).ToVec3(0);
			const point_world = objPos.Plus(point_withObjRotApplied);
			const dist = mousePos_world.ToVec2().DistanceTo(point_world.ToVec2());

			if (dist < closestDist && dist <= maxDist) {
				closestDist = dist;
				closestIndex = i;
			}
		}

		return closestIndex;
	}
}

async function PushPendingPathUpdateForObj(obj: MapObject, update: ObjPathUpdate, state: ToolState_PathModify) {
	const newPaths = Clone(obj.paths) as ObjPath[];
	newPaths[update.pathIndex].points = update.NewPoints();
	update.sendStarted = true; // mark as started, so that if a new update starts while this is being sent, this one's new-points get integrated as the base-points for the next update
	await new UpdateMapObject({
		id: obj.id,
		updates: {paths: newPaths},
	}).RunOnServer();

	// if the update was not overridden by a new pending update, remove it from the pending list (just for data-cleanliness/accuracy purposes; not strictly necessary)
	if (state.pendingObjPathUpdates.get(obj.id) == update) {
		RunInAction("PushPendingPathUpdateForObj", ()=>state.pendingObjPathUpdates.delete(obj.id));
	}
}